import {makeStyles} from "@material-ui/core/styles";
import React from "react";
import moment from "moment";
import Accordion from "@material-ui/core/Accordion";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import {toDollars} from "../../utils";
import ReceiptIcon from "@material-ui/icons/Receipt";
import {Divider} from "@material-ui/core";
import {DateRangeIcon} from "@material-ui/pickers/_shared/icons/DateRangeIcon";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import DetailsData from "./DetailsData";
import AccordionActions from "@material-ui/core/AccordionActions";
import Button from "@material-ui/core/Button";
import PropTypes from "prop-types";
import {SummaryDetailData} from "./shared.propTypes";

const useStyles = makeStyles({
    root: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column'
    },
    panelSummary: {
        width: '100%',
        flexGrow: 1,
        flexShrink: 1,
        display: 'flex',
        flexWrap: 'wrap',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    panelDetails: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column'
    },
    summaryTitle: {
        display: 'flex',
        flexGrow: 1,
        flexShrink: 1,
        flexDirection: 'row',
        justifyContent: 'flex-start'
    },
    summaryAmount: {
        display: 'flex',
        flexGrow: 1,
        flexShrink: 1,
        flexDirection: 'row',
        justifyContent: 'space-evenly',
    },
    iconAmount: {
        display: 'flex',
        justifyContent: 'space-evenly',
    },
});

const setDataCyText = (summaryType) => {
    return summaryType === 'billing' ? 'billing' : 'new-parker';
}

function SummaryData({
    type,
    label,
    initial,
    expanded,
    recurring,
    startDate,
    paymentDay = 1,
    billingDay = 15,
    secondMonthPayment,
    actions,
    parkers = [],
}) {
    const cls = useStyles();
    const getCaption = (detailType) => {
        const now = moment();
        const start = moment(startDate, 'YYYY-MM-DD');
        const fmt = 'MM/DD';

        const parkerStartsThisMonth = type === 'billing' ?
            parkers.some(({ startDate: s }) => s && moment(s, 'YYYY-MM-DD').isSame(now, 'month')) :
            start.isSame(now, 'month');
        
        const includeNextMonthInPayment = (
            (billingDay <= Number(now.format('D'))) &&
            secondMonthPayment &&
            parkerStartsThisMonth
        );

        if (type === 'billing') {
            if (detailType === 'initial') {
                if (includeNextMonthInPayment) {
                    return 'Due today upon purchase, this amount includes the prorated first month and full second month.'
                }
                return 'Due today upon purchase.'
            } else {
                return `Due on the ${moment().date(paymentDay).format('Do')} of each month.`;
            }
        } else {
            if (detailType === 'initial') {
                return `Prorated from ${start.format(fmt)} to ${start.endOf('M').format(fmt)}`;
            } else {
                const addedMonths = includeNextMonthInPayment ? 2 : 1;
                const paymentStartDay = moment(start).add(addedMonths, 'month').date(paymentDay);
                return `Due monthly starting ${paymentStartDay.format(fmt)}`
            }
        }
    }
    return <Accordion className={cls.root} expanded={type === 'billing' ? true : undefined} defaultExpanded={expanded}>
        <AccordionSummary expandIcon={type === 'parker' ? <ExpandMoreIcon/> : undefined}>
            <Box className={cls.panelSummary}>
                <Box className={cls.summaryTitle}>
                    <Typography variant='body1' data-cy={`${setDataCyText(type)}-title-text`}>{label}</Typography>
                </Box>
                <Box className={cls.summaryAmount}>
                    <Box className={cls.iconAmount}>
                        <Typography variant='body2' data-cy="initial-fee-value-text">{toDollars(initial.total)}</Typography>
                        <ReceiptIcon fontSize="small"/>
                    </Box>
                    <Divider orientation="vertical" flexItem/>
                    <Box className={cls.iconAmount}>
                        <Typography variant='body2' data-cy="recurring-fee-value-text">{toDollars(recurring.total)}</Typography>
                        <DateRangeIcon fontSize="small"/>
                    </Box>
                </Box>
            </Box>
        </AccordionSummary>
        <AccordionDetails className={cls.panelDetails}>
            {!!initial.total &&
            <DetailsData type="initial" data={initial} info={getCaption('initial')}/>}
            {!!recurring.total &&
            <DetailsData type="recurring" data={recurring} info={getCaption('recurring')}/>}
        </AccordionDetails>
        <AccordionActions>
            {actions.map(({onClick, disabled, label, isPrimary}, i) => (
                <Button key={i} onClick={() => onClick()}
                        disabled={disabled}
                        color={isPrimary ? "primary" : "default"}
                        variant={type === "parker" ? "outlined" : "contained"}
                        size={type === "parker" ? "small" : "medium"}>{label}</Button>
            ))}
        </AccordionActions>
    </Accordion>
}

SummaryData.propTypes = {
    type: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    initial: SummaryDetailData.isRequired,
    recurring: SummaryDetailData.isRequired,
    startDate: PropTypes.any.isRequired,
    expanded: PropTypes.bool,
    paymentDay: PropTypes.any.isRequired,
    actions: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string.isRequired,
        onClick: PropTypes.func.isRequired,
        disabled: PropTypes.bool,
        isPrimary: PropTypes.bool,
    })).isRequired
}

export default React.memo(SummaryData);
